import { Component, Inject, OnInit, Optional } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  DialogRef,
  DIALOG_DATA,
} from '@cyborg-monorepo/common/angular/cyborg-ui';
import {
  CollectionRecord,
  Enums,
  Environment,
  ExpandedHuntPackageRecord,
  HuntTemplate,
  Profile,
} from '@cyborg-monorepo/hunter/common';
import { forkJoin, map, Observable } from 'rxjs';
import { isHuntPackageHuntCompatible } from '../../../../../helpers/utils';
import { CompanyService } from '../../../services/company.service';
import { ContentService } from '../../../services/content.service';
import { EnvironmentService } from '../../../services/environment.service';
import { HuntTemplateService } from '../../../services/hunt-template.service';

@Component({ standalone: false, 
  selector: 'new-template-dialog',
  templateUrl: './new-template-dialog.component.html',
  styleUrls: ['./new-template-dialog.component.scss'],
})
export class NewTemplateDialogComponent implements OnInit {
  public formGroup: FormGroup;
  public priorities: Array<string> = [];
  public repeatOptions: Array<string> = ['Daily', 'Weekly', 'Monthly'];
  public everyOptions: Array<number> = [...Array(99).keys()];
  public generatedHuntPackages: ExpandedHuntPackageRecord[];
  public placeholderName: string = '';
  public assignees$: Observable<Array<Profile>>;
  public placeholderHypothesis: string;
  public placeholderDescription: string;
  public error: string;
  public environments: Environment[];

  constructor(
    public dialogRef: DialogRef,
    private formBuilder: FormBuilder,
    private huntTemplateService: HuntTemplateService,
    private contentService: ContentService,
    private companyService: CompanyService,
    private environmentService: EnvironmentService,
    @Optional()
    @Inject(DIALOG_DATA)
    public data?: {
      huntPackages: Array<ExpandedHuntPackageRecord>;
      collection: CollectionRecord;
    },
  ) {}

  public ngOnInit(): void {
    this.environmentService.getAllEnvironments().subscribe((environments) => {
      this.environments = environments;
    });

    if (this.data?.collection) {
      this.placeholderName = this.truncateString(
        this.data.collection.title,
        50,
      );
      this.placeholderDescription = this.data.collection.threat_summary;
      this.placeholderHypothesis = this.data.collection.synopsis;
    } else {
      if (this.data?.huntPackages.length === 1) {
        this.placeholderName = this.truncateString(
          this.data.huntPackages[0].title,
          50,
        );
        this.placeholderHypothesis = this.data.huntPackages[0].description;
        this.placeholderDescription =
          this.data.huntPackages[0].context.threat_description;
      }
    }

    this.priorities = Object.values(Enums.PriorityEnum);
    this.formGroup = this.formBuilder.group({
      name: [this.placeholderName, Validators.required],
      environments: new FormControl(''),
      priority: [Enums.PriorityEnum.LOW, Validators.required],
      hypothesis: [
        this.placeholderHypothesis ? this.placeholderHypothesis : null,
        Validators.required,
      ],
      description: [
        this.placeholderDescription ? this.placeholderDescription : null,
        Validators.required,
      ],
      defaultAssignee: [],
    });

    this.assignees$ = this.companyService.getUsers().pipe(
      map((users: Array<Profile>) => {
        return users.map((user: Profile) => {
          return {
            ...user,
            ...{ fullName: user.firstName + ' ' + user.lastName },
          };
        });
      }),
    );

    this.generatedHuntPackages = this.data?.huntPackages.filter(
      (huntPackage) => {
        return isHuntPackageHuntCompatible(huntPackage);
      },
    );
  }

  public onValueChange(): void {
    const generatedHuntPackages: Observable<ExpandedHuntPackageRecord>[] =
      this.data?.huntPackages
        .filter((huntPackage) => {
          return isHuntPackageHuntCompatible(huntPackage);
        })
        .map((huntPackage) => {
          return this.contentService
            .getHuntPackageContent(huntPackage.UUID, {
              environments: this.formGroup.value.environments.map(
                (env) => env.id,
              ),
            })
            .pipe(
              map((generatedContent) => {
                const expanded = huntPackage as ExpandedHuntPackageRecord;
                expanded.analysis = expanded.originalAnalysis =
                  generatedContent;
                return expanded;
              }),
            );
        });
    forkJoin(generatedHuntPackages).subscribe((generatedHuntPacks) => {
      this.generatedHuntPackages = generatedHuntPacks;
    });
  }

  public onSubmit(): void {
    const template: HuntTemplate = this.formGroup.value;

    if (this.generatedHuntPackages) {
      template.huntPackages = this.generatedHuntPackages;
    }
    this.huntTemplateService.createHuntTemplate(template).subscribe(
      (template: HuntTemplate) => {
        this.dialogRef.close(template);
      },
      (error) => {
        this.error = error.message;
      },
    );
  }

  public truncateString(string, limit): string {
    if (string.length > limit) {
      return string.substring(0, limit - 3) + '...';
    } else {
      return string;
    }
  }

  public onClose(): void {
    this.dialogRef.close();
  }
}
